import React, { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import {
  FileText,
  Boxes,
  History,
  AlertTriangle,
  DollarSign,
  Clock,
  ShieldAlert,
  Wrench,
  RotateCcw,
  ArrowRightLeft,
  CheckCircle2,
  Download,
  Printer,
  Filter,
  RefreshCw,
  Search,
  Calendar,
  ChevronLeft,
  ChevronRight,
  FileSpreadsheet,
  X,
  Layers,
  ArrowUpRight,
  TrendingDown,
} from "lucide-react";

const API = process.env.REACT_APP_API_URL;

// ─── 10 Standard Inventory Reports Configuration ──────────────────────────────
const REPORT_TABS = [
  {
    id: "current-stock",
    num: "01",
    title: "Current Stock",
    icon: Boxes,
    subtitle: "On-hand stock levels, category distribution, valuation and SKU status",
  },
  {
    id: "stock-ledger",
    num: "02",
    title: "Stock Ledger",
    icon: History,
    subtitle: "Complete transaction audit trail across receipts, sales, movements & users",
  },
  {
    id: "low-stock",
    num: "03",
    title: "Low Stock",
    icon: AlertTriangle,
    subtitle: "Reorder alerts, deficit calculation and replenishment thresholds",
  },
  {
    id: "valuation",
    num: "04",
    title: "Stock Valuation",
    icon: DollarSign,
    subtitle: "Inventory balance sheet valuation using Retail M.R.P or Wholesale cost basis",
  },
  {
    id: "ageing",
    num: "05",
    title: "Ageing & Slow-Moving",
    icon: Clock,
    subtitle: "Longevity buckets (0-30d, 31-60d, 61-90d, 91-180d, 180+d) and dead stock identification",
  },
  {
    id: "damage-loss",
    num: "06",
    title: "Damage & Loss",
    icon: ShieldAlert,
    subtitle: "Damaged/lost inventory, financial loss impact, bin location & disposition",
  },
  {
    id: "repair",
    num: "07",
    title: "Repair & Refurbish",
    icon: Wrench,
    subtitle: "Open & closed repair jobs, service technicians, and turnaround tracking",
  },
  {
    id: "returns",
    num: "08",
    title: "Returns (RMA)",
    icon: RotateCcw,
    subtitle: "Customer & supplier returns, restock condition assessment & RMA references",
  },
  {
    id: "transfer",
    num: "09",
    title: "Warehouse Transfer",
    icon: ArrowRightLeft,
    subtitle: "Inter-facility transfer logs between Central Hub and Regional Fulfilment Centers",
  },
  {
    id: "reconciliation",
    num: "10",
    title: "Reconciliation & Audit",
    icon: CheckCircle2,
    subtitle: "Cycle count audits, system vs physical variance analysis & approved adjustments",
  },
];

const InventoryReports = () => {
  // Active Tab
  const [activeTab, setActiveTab] = useState("current-stock");

  // Data & State
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [metaSummary, setMetaSummary] = useState(null);
  const [ageingBuckets, setAgeingBuckets] = useState(null);

  // Filter Options from Server
  const [options, setOptions] = useState({
    warehouses: [],
    vendors: [],
    categories: [],
  });

  // Dynamic Filters
  const [filters, setFilters] = useState({
    search: "",
    warehouse: "all",
    owner: "all",
    vendorId: "all",
    category: "all",
    status: "all",
    sku: "",
    dateFrom: "",
    dateTo: "",
    type: "all",
    threshold: "20",
    valuationBasis: "retail",
    bucket: "all",
    performedBy: "",
    reference: "",
  });

  // Load filter dropdown options once
  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const res = await axios.get(`${API}/inventory/filter-options`);
        if (res.data.success) {
          setOptions({
            warehouses: res.data.warehouses || [],
            vendors: res.data.vendors || [],
            categories: res.data.categories || [],
          });
        }
      } catch (err) {
        console.error("Failed to fetch filter options:", err);
      }
    };
    fetchOptions();
  }, []);

  // Reset pagination on tab change or filter change
  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setPage(1);
    setFilters((prev) => ({
      ...prev,
      search: "",
      sku: "",
      bucket: "all",
      type: "all",
    }));
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const handleResetFilters = () => {
    setFilters({
      search: "",
      warehouse: "all",
      owner: "all",
      vendorId: "all",
      category: "all",
      status: "all",
      sku: "",
      dateFrom: "",
      dateTo: "",
      type: "all",
      threshold: "20",
      valuationBasis: "retail",
      bucket: "all",
      performedBy: "",
      reference: "",
    });
    setPage(1);
  };

  // Fetch Report Data based on active tab and filters
  const fetchReportData = async () => {
    setLoading(true);
    try {
      let endpoint = "";
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
      });

      if (filters.search) params.append("search", filters.search);
      if (filters.warehouse && filters.warehouse !== "all") params.append("warehouse", filters.warehouse);
      if (filters.owner && filters.owner !== "all") params.append("owner", filters.owner);
      if (filters.vendorId && filters.vendorId !== "all") params.append("vendorId", filters.vendorId);
      if (filters.category && filters.category !== "all") params.append("category", filters.category);
      if (filters.sku) params.append("sku", filters.sku);

      if (activeTab === "current-stock") {
        endpoint = `${API}/inventory/reports/current-stock`;
        if (filters.status && filters.status !== "all") params.append("status", filters.status);
      } else if (activeTab === "stock-ledger") {
        endpoint = `${API}/inventory/reports/stock-ledger`;
        if (filters.type && filters.type !== "all") params.append("type", filters.type);
        if (filters.dateFrom) params.append("dateFrom", filters.dateFrom);
        if (filters.dateTo) params.append("dateTo", filters.dateTo);
        if (filters.reference) params.append("reference", filters.reference);
      } else if (activeTab === "low-stock") {
        endpoint = `${API}/inventory/reports/low-stock`;
        if (filters.threshold) params.append("threshold", filters.threshold);
      } else if (activeTab === "valuation") {
        endpoint = `${API}/inventory/reports/valuation`;
        params.append("valuationBasis", filters.valuationBasis);
      } else if (activeTab === "ageing") {
        endpoint = `${API}/inventory/reports/ageing`;
        if (filters.bucket && filters.bucket !== "all") params.append("bucket", filters.bucket);
      } else if (activeTab === "damage-loss") {
        endpoint = `${API}/inventory/reports/stock-ledger`;
        params.append("types", "Damage,Loss");
        if (filters.dateFrom) params.append("dateFrom", filters.dateFrom);
        if (filters.dateTo) params.append("dateTo", filters.dateTo);
      } else if (activeTab === "repair") {
        endpoint = `${API}/inventory/reports/stock-ledger`;
        params.append("type", "Repair");
        if (filters.dateFrom) params.append("dateFrom", filters.dateFrom);
        if (filters.dateTo) params.append("dateTo", filters.dateTo);
      } else if (activeTab === "returns") {
        endpoint = `${API}/inventory/reports/stock-ledger`;
        params.append("type", "Return");
        if (filters.dateFrom) params.append("dateFrom", filters.dateFrom);
        if (filters.dateTo) params.append("dateTo", filters.dateTo);
      } else if (activeTab === "transfer") {
        endpoint = `${API}/inventory/reports/stock-ledger`;
        params.append("type", "Transfer");
        if (filters.dateFrom) params.append("dateFrom", filters.dateFrom);
        if (filters.dateTo) params.append("dateTo", filters.dateTo);
      } else if (activeTab === "reconciliation") {
        endpoint = `${API}/inventory/reports/stock-ledger`;
        params.append("type", "Adjustment");
        if (filters.dateFrom) params.append("dateFrom", filters.dateFrom);
        if (filters.dateTo) params.append("dateTo", filters.dateTo);
      }

      const res = await axios.get(`${endpoint}?${params.toString()}`);
      if (res.data.success) {
        setData(res.data.data || []);
        setTotalItems(res.data.total || 0);
        setTotalPages(res.data.totalPages || 1);
        setMetaSummary(res.data.summary || null);
        if (res.data.buckets) setAgeingBuckets(res.data.buckets);
      } else {
        setData([]);
        setTotalItems(0);
        setTotalPages(1);
      }
    } catch (err) {
      console.error("Error fetching report data:", err);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReportData();
  }, [activeTab, page, limit, filters.threshold, filters.valuationBasis, filters.bucket]);

  // Handle Export to CSV
  const handleExportCSV = () => {
    if (!data || data.length === 0) {
      alert("No data available to export.");
      return;
    }

    const flatRows = getFlatRowsForExport(data, activeTab);
    if (!flatRows || flatRows.length === 0) return;

    const headers = Object.keys(flatRows[0]);
    const csvContent = [
      headers.join(","),
      ...flatRows.map((row) =>
        headers
          .map((h) => {
            const val = row[h] !== undefined && row[h] !== null ? String(row[h]) : "";
            return `"${val.replace(/"/g, '""')}"`;
          })
          .join(",")
      ),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `bharat_karigari_${activeTab}_report_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Handle Export to Excel (.xlsx)
  const handleExportExcel = () => {
    if (!data || data.length === 0) {
      alert("No data available to export.");
      return;
    }
    const flatRows = getFlatRowsForExport(data, activeTab);
    const ws = XLSX.utils.json_to_sheet(flatRows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Report");
    XLSX.writeFile(wb, `bharat_karigari_${activeTab}_report_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  // Handle Print
  const handlePrint = () => {
    window.print();
  };

  // Build clean flat rows for export
  const getFlatRowsForExport = (rows, tab) => {
    return rows.map((r, i) => {
      if (tab === "current-stock") {
        return {
          "#": i + 1,
          SKU: r.sku,
          Product: r.name,
          Category: r.category,
          Warehouse: r.warehouse,
          Rack_Bin: r.rackBinLocation,
          Owner: r.owner,
          Vendor: r.vendorName,
          Stock: r.stock,
          Price_INR: r.price,
          Total_Value_INR: r.totalValue,
          Status: r.status,
        };
      }
      if (tab === "stock-ledger") {
        return {
          "#": i + 1,
          Txn_ID: r.transactionId,
          Date: new Date(r.date).toLocaleString("en-IN"),
          Product: r.productName,
          SKU: r.sku,
          Type: r.type,
          Quantity: r.quantity,
          Before_Qty: r.beforeQuantity,
          After_Qty: r.afterQuantity,
          Warehouse: r.warehouse,
          Performed_By: r.performedBy,
          Reference: r.referenceId,
          Note: r.note,
        };
      }
      if (tab === "low-stock") {
        return {
          "#": i + 1,
          SKU: r.sku,
          Product: r.name,
          Category: r.category,
          Warehouse: r.warehouse,
          Owner: r.owner,
          Vendor: r.vendorName,
          Current_Stock: r.stock,
          Reorder_Threshold: r.reorderThreshold,
          Shortage_Deficit: r.shortage,
          Status: r.status,
        };
      }
      if (tab === "valuation") {
        return {
          "#": i + 1,
          SKU: r.sku,
          Product: r.name,
          Category: r.category,
          Warehouse: r.warehouse,
          Owner: r.owner,
          Vendor: r.vendorName,
          Stock_Units: r.stock,
          Unit_Valuation_INR: r.unitValuation,
          Basis: r.valuationBasis,
          Total_Valuation_INR: r.totalValuation,
        };
      }
      if (tab === "ageing") {
        return {
          "#": i + 1,
          SKU: r.sku,
          Product: r.name,
          Category: r.category,
          Warehouse: r.warehouse,
          Owner: r.owner,
          Vendor: r.vendorName,
          Stock: r.stock,
          Days_In_Stock: r.daysInStock,
          Age_Bucket: r.ageBucket,
          Slow_Moving: r.isSlowMoving ? "YES" : "NO",
        };
      }
      if (tab === "damage-loss") {
        return {
          "#": i + 1,
          Txn_ID: r.transactionId,
          Date: new Date(r.date).toLocaleString("en-IN"),
          Product: r.productName,
          SKU: r.sku,
          Type: r.type,
          Quantity: r.quantity,
          Est_Value_Loss_INR: r.totalValue,
          Warehouse: r.warehouse,
          Location: r.locationCode,
          Owner: r.owner,
          Vendor: r.vendorName,
          Reported_By: r.performedBy,
          Reason_Note: r.note,
        };
      }
      if (tab === "repair") {
        return {
          "#": i + 1,
          Txn_ID: r.transactionId,
          Date: new Date(r.date).toLocaleString("en-IN"),
          Product: r.productName,
          SKU: r.sku,
          Quantity: r.quantity,
          Warehouse: r.warehouse,
          Location: r.locationCode,
          Job_Ref: r.referenceId,
          Handled_By: r.performedBy,
          Note: r.note,
        };
      }
      if (tab === "returns") {
        return {
          "#": i + 1,
          Txn_ID: r.transactionId,
          Date: new Date(r.date).toLocaleString("en-IN"),
          Product: r.productName,
          SKU: r.sku,
          Quantity: r.quantity,
          Warehouse: r.warehouse,
          Owner: r.owner,
          Vendor: r.vendorName,
          RMA_Order_Ref: r.referenceId,
          Received_By: r.performedBy,
          Reason: r.note,
        };
      }
      if (tab === "transfer") {
        return {
          "#": i + 1,
          Txn_ID: r.transactionId,
          Date: new Date(r.date).toLocaleString("en-IN"),
          Product: r.productName,
          SKU: r.sku,
          Quantity: r.quantity,
          Source_Warehouse: r.warehouse,
          Destination_Warehouse: r.warehouseTo,
          Transferred_By: r.performedBy,
          Waybill_Ref: r.referenceId,
          Status: "Completed",
        };
      }
      if (tab === "reconciliation") {
        return {
          "#": i + 1,
          Txn_ID: r.transactionId,
          Date: new Date(r.date).toLocaleString("en-IN"),
          Product: r.productName,
          SKU: r.sku,
          System_Qty: r.beforeQuantity,
          Physical_Count: r.afterQuantity,
          Variance: r.quantity,
          Warehouse: r.warehouse,
          Auditor: r.performedBy,
          Audit_Note: r.note,
        };
      }
      return r;
    });
  };

  const activeTabMeta = useMemo(() => {
    return REPORT_TABS.find((t) => t.id === activeTab) || REPORT_TABS[0];
  }, [activeTab]);

  return (
    <section className="min-h-screen bg-[#f8f9fa] p-3 sm:p-6 font-sans text-[#0F1111]">
      {/* ─── PRINT ONLY STYLES ──────────────────────────────────────────────── */}
      <style>{`
        @media print {
          body {
            background: #fff !important;
            color: #000 !important;
          }
          nav, aside, .no-print {
            display: none !important;
          }
          .print-header {
            display: block !important;
          }
          .table-container {
            overflow: visible !important;
            border: 1px solid #000 !important;
          }
          th, td {
            font-size: 10px !important;
            padding: 4px 6px !important;
            border: 1px solid #ccc !important;
          }
        }
        @media screen {
          .print-header {
            display: none;
          }
        }
      `}</style>

      {/* ─── PRINT HEADER ─────────────────────────────────────────────────── */}
      <div className="print-header mb-6 border-b pb-4">
        <h1 className="text-2xl font-bold">BHARAT KARIGARI — INVENTORY INTELLIGENCE</h1>
        <h2 className="text-lg font-semibold text-gray-700">
          Report #{activeTabMeta.num}: {activeTabMeta.title}
        </h2>
        <p className="text-xs text-gray-500">
          Generated on: {new Date().toLocaleString("en-IN")} | Warehouse Filter: {filters.warehouse} | Owner:{" "}
          {filters.owner}
        </p>
      </div>

      {/* ─── SCREEN TOP HEADER ────────────────────────────────────────────── */}
      <div className="no-print flex flex-col md:flex-row md:items-center justify-between pb-4 mb-4 border-b border-gray-200 gap-4">
        <div>
          <div className="flex items-center gap-2">
            <span className="px-2.5 py-0.5 text-xs font-bold bg-[#b07d17] text-white rounded-md tracking-wider">
              SECTION 13
            </span>
            <h1 className="text-2xl md:text-3xl font-philosopher font-bold text-gray-900 tracking-tight flex items-center gap-2.5">
              <FileText className="text-[#b07d17] w-7 h-7 inline-block" />
              Inventory Reports & Audits
            </h1>
          </div>
          <p className="text-xs md:text-sm text-gray-600 mt-1">
            Enterprise analytics, ledger audit trails, valuation balance sheets & exportable operational logs.
          </p>
        </div>

        {/* Global Export & Action Buttons */}
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={fetchReportData}
            title="Refresh current report"
            className="flex items-center gap-1.5 px-3 py-2 bg-white hover:bg-gray-50 border border-gray-300 text-gray-700 rounded-md text-xs font-medium shadow-sm transition active:scale-95"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin text-[#b07d17]" : ""}`} />
            Refresh
          </button>
          <button
            onClick={handleExportCSV}
            title="Export CSV document"
            className="flex items-center gap-1.5 px-3.5 py-2 bg-white hover:bg-emerald-50 border border-emerald-300 text-emerald-800 rounded-md text-xs font-semibold shadow-sm transition active:scale-95"
          >
            <Download className="w-3.5 h-3.5 text-emerald-600" />
            Export CSV
          </button>
          <button
            onClick={handleExportExcel}
            title="Export Excel workbook (.xlsx)"
            className="flex items-center gap-1.5 px-3.5 py-2 bg-emerald-700 hover:bg-emerald-800 text-white rounded-md text-xs font-semibold shadow-sm transition active:scale-95"
          >
            <FileSpreadsheet className="w-3.5 h-3.5" />
            Excel (.xlsx)
          </button>
          <button
            onClick={handlePrint}
            title="Print or Save as PDF"
            className="flex items-center gap-1.5 px-3.5 py-2 bg-[#131921] hover:bg-[#232f3e] text-white rounded-md text-xs font-semibold shadow-sm transition active:scale-95"
          >
            <Printer className="w-3.5 h-3.5" />
            Print / PDF
          </button>
        </div>
      </div>

      {/* ─── 10 REPORT TABS STRIP (AMAZON ENTERPRISE STYLE) ───────────────── */}
      <div className="no-print bg-white border border-[#D5D9D9] rounded-xl shadow-sm mb-5 p-2 overflow-hidden">
        <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-thin scrollbar-thumb-gray-300">
          {REPORT_TABS.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={`flex items-center gap-2.5 px-3.5 py-2.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-all duration-150 flex-shrink-0 ${
                  isActive
                    ? "bg-[#b07d17] text-white shadow-md ring-2 ring-[#b07d17]/30"
                    : "bg-gray-50 hover:bg-gray-100 text-gray-700 border border-gray-200"
                }`}
              >
                <span
                  className={`text-[10px] px-1.5 py-0.5 rounded font-mono font-bold ${
                    isActive ? "bg-black/20 text-white" : "bg-gray-200 text-gray-600"
                  }`}
                >
                  {tab.num}
                </span>
                <Icon className={`w-4 h-4 ${isActive ? "text-white" : "text-[#b07d17]"}`} />
                <span>{tab.title}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* ─── ACTIVE REPORT BANNER & DESCRIPTION ──────────────────────────── */}
      <div className="no-print bg-gradient-to-r from-amber-50 via-white to-orange-50 border border-amber-200/80 rounded-xl p-4 mb-4 flex flex-col md:flex-row md:items-center justify-between gap-3 shadow-xs">
        <div className="flex items-start gap-3">
          <div className="p-2.5 bg-white border border-amber-300 rounded-lg shadow-xs text-[#b07d17]">
            {React.createElement(activeTabMeta.icon, { className: "w-5 h-5" })}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold text-[#b07d17] uppercase tracking-wide">
                Report {activeTabMeta.num}
              </span>
              <span className="text-gray-300">|</span>
              <h2 className="text-base font-bold text-gray-900">{activeTabMeta.title}</h2>
            </div>
            <p className="text-xs text-gray-600 mt-0.5">{activeTabMeta.subtitle}</p>
          </div>
        </div>

        {/* Live records counter badge */}
        <div className="flex items-center gap-2 self-start md:self-auto">
          <span className="px-3 py-1 bg-white border border-gray-300 text-gray-700 rounded-full text-xs font-medium shadow-2xs">
            Total Records: <strong className="text-gray-900">{totalItems}</strong>
          </span>
        </div>
      </div>

      {/* ─── CONTEXTUAL KPI / SUMMARY CARDS ──────────────────────────────── */}
      {/* 1. Current Stock KPI Bar */}
      {activeTab === "current-stock" && metaSummary && (
        <div className="no-print grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <div className="bg-white border border-gray-200 rounded-lg p-3 shadow-xs">
            <span className="text-[11px] font-medium text-gray-500 uppercase">Total Catalog SKUs</span>
            <div className="text-xl font-bold text-gray-900 mt-1">{metaSummary.totalItems ?? totalItems}</div>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-3 shadow-xs">
            <span className="text-[11px] font-medium text-gray-500 uppercase">On-Hand Stock Units</span>
            <div className="text-xl font-bold text-blue-700 mt-1">{metaSummary.totalStock ?? "--"}</div>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-3 shadow-xs">
            <span className="text-[11px] font-medium text-gray-500 uppercase">Inventory Holding Value</span>
            <div className="text-xl font-bold text-emerald-700 mt-1">
              ₹{(metaSummary.totalValue || 0).toLocaleString("en-IN")}
            </div>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-3 shadow-xs">
            <span className="text-[11px] font-medium text-gray-500 uppercase">Warehouses Covered</span>
            <div className="text-xl font-bold text-[#b07d17] mt-1">{options.warehouses.length || 1} Hubs</div>
          </div>
        </div>
      )}

      {/* 2. Valuation KPI Bar */}
      {activeTab === "valuation" && metaSummary && (
        <div className="no-print grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
          <div className="bg-white border border-blue-200 rounded-lg p-3 shadow-xs">
            <span className="text-[11px] font-medium text-gray-500 uppercase">Valued Products</span>
            <div className="text-2xl font-bold text-blue-700 mt-1">{metaSummary.totalProducts ?? totalItems}</div>
          </div>
          <div className="bg-white border border-purple-200 rounded-lg p-3 shadow-xs">
            <span className="text-[11px] font-medium text-gray-500 uppercase">Total Physical Units</span>
            <div className="text-2xl font-bold text-purple-700 mt-1">{metaSummary.totalStock ?? 0} units</div>
          </div>
          <div className="bg-white border border-emerald-300 bg-emerald-50/40 rounded-lg p-3 shadow-xs">
            <span className="text-[11px] font-semibold text-emerald-800 uppercase flex items-center justify-between">
              <span>Grand Valuation ({filters.valuationBasis.toUpperCase()})</span>
              <span className="text-[10px] bg-emerald-200/80 text-emerald-900 px-1.5 py-0.5 rounded font-normal">
                Live Book
              </span>
            </span>
            <div className="text-2xl font-black text-emerald-800 mt-1">
              ₹{(metaSummary.totalValuation || 0).toLocaleString("en-IN")}
            </div>
          </div>
        </div>
      )}

      {/* 3. Ageing Interactive Buckets Strip */}
      {activeTab === "ageing" && ageingBuckets && (
        <div className="no-print grid grid-cols-2 sm:grid-cols-5 gap-2.5 mb-4">
          {[
            { key: "0-30", label: "0–30 Days", tag: "Fast / Fresh", bg: "hover:bg-emerald-50", color: "text-emerald-700" },
            { key: "31-60", label: "31–60 Days", tag: "Normal", bg: "hover:bg-blue-50", color: "text-blue-700" },
            { key: "61-90", label: "61–90 Days", tag: "Slow Moving", bg: "hover:bg-amber-50", color: "text-amber-700" },
            { key: "91-180", label: "91–180 Days", tag: "Critical", bg: "hover:bg-orange-50", color: "text-orange-700" },
            { key: "180+", label: "180+ Days", tag: "Stagnant Stock", bg: "hover:bg-red-50", color: "text-red-700" },
          ].map((b) => {
            const isSelected = filters.bucket === b.key;
            const bData = ageingBuckets[b.key] || { count: 0, stock: 0 };
            return (
              <button
                key={b.key}
                onClick={() => handleFilterChange("bucket", isSelected ? "all" : b.key)}
                className={`p-3 rounded-lg border text-left transition-all ${
                  isSelected
                    ? "border-[#b07d17] bg-amber-50 shadow-sm ring-1 ring-[#b07d17]"
                    : `bg-white border-gray-200 ${b.bg}`
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-[11px] font-bold text-gray-700">{b.label}</span>
                  <span className={`text-[9px] font-semibold px-1 py-0.5 rounded bg-gray-100 ${b.color}`}>
                    {b.tag}
                  </span>
                </div>
                <div className="text-lg font-bold text-gray-900 mt-1">{bData.count} SKUs</div>
                <div className="text-[11px] text-gray-500">{bData.stock} total units</div>
              </button>
            );
          })}
        </div>
      )}

      {/* ─── DYNAMIC FILTER BAR ───────────────────────────────────────────── */}
      <div className="no-print bg-white border border-[#D5D9D9] rounded-xl shadow-xs p-4 mb-5">
        <div className="flex items-center justify-between mb-3 border-b border-gray-100 pb-2">
          <div className="flex items-center gap-1.5 text-xs font-bold text-gray-800">
            <Filter className="w-3.5 h-3.5 text-[#b07d17]" />
            <span>Filter Parameters</span>
          </div>
          <button
            onClick={handleResetFilters}
            className="text-xs text-gray-500 hover:text-red-600 flex items-center gap-1 font-medium transition"
          >
            <X className="w-3 h-3" />
            Reset All Filters
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {/* Universal Search */}
          <div className="lg:col-span-2">
            <label className="block text-[11px] font-semibold text-gray-600 mb-1">Search Keywords</label>
            <div className="relative">
              <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-2.5" />
              <input
                type="text"
                value={filters.search}
                onChange={(e) => handleFilterChange("search", e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && fetchReportData()}
                placeholder="Product name, SKU, Txn ID..."
                className="w-full pl-8 pr-2 py-1.5 text-xs border border-gray-300 rounded-md focus:border-[#b07d17] focus:ring-1 focus:ring-[#b07d17] outline-none"
              />
            </div>
          </div>

          {/* Warehouse Selector */}
          <div>
            <label className="block text-[11px] font-semibold text-gray-600 mb-1">Warehouse</label>
            <select
              value={filters.warehouse}
              onChange={(e) => handleFilterChange("warehouse", e.target.value)}
              className="w-full py-1.5 px-2 text-xs border border-gray-300 rounded-md focus:border-[#b07d17] outline-none bg-white"
            >
              <option value="all">All Warehouses</option>
              {options.warehouses.map((w) => (
                <option key={w.id} value={w.name}>
                  {w.name}
                </option>
              ))}
            </select>
          </div>

          {/* Owner Filter (Admin / Vendor) */}
          {(activeTab === "current-stock" ||
            activeTab === "stock-ledger" ||
            activeTab === "valuation" ||
            activeTab === "damage-loss") && (
            <div>
              <label className="block text-[11px] font-semibold text-gray-600 mb-1">Stock Owner</label>
              <select
                value={filters.owner}
                onChange={(e) => handleFilterChange("owner", e.target.value)}
                className="w-full py-1.5 px-2 text-xs border border-gray-300 rounded-md focus:border-[#b07d17] outline-none bg-white"
              >
                <option value="all">All Owners</option>
                <option value="Admin">Admin (In-House)</option>
                <option value="Vendor">Vendor (Suppliers)</option>
              </select>
            </div>
          )}

          {/* Vendor Filter */}
          {(activeTab === "current-stock" ||
            activeTab === "low-stock" ||
            activeTab === "valuation" ||
            activeTab === "damage-loss" ||
            activeTab === "repair" ||
            activeTab === "returns") && (
            <div>
              <label className="block text-[11px] font-semibold text-gray-600 mb-1">Vendor / Artisan</label>
              <select
                value={filters.vendorId}
                onChange={(e) => handleFilterChange("vendorId", e.target.value)}
                className="w-full py-1.5 px-2 text-xs border border-gray-300 rounded-md focus:border-[#b07d17] outline-none bg-white"
              >
                <option value="all">All Vendors</option>
                {options.vendors.map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Category Filter */}
          {(activeTab === "current-stock" ||
            activeTab === "low-stock" ||
            activeTab === "valuation" ||
            activeTab === "ageing") && (
            <div>
              <label className="block text-[11px] font-semibold text-gray-600 mb-1">Product Category</label>
              <select
                value={filters.category}
                onChange={(e) => handleFilterChange("category", e.target.value)}
                className="w-full py-1.5 px-2 text-xs border border-gray-300 rounded-md focus:border-[#b07d17] outline-none bg-white"
              >
                <option value="all">All Categories</option>
                {options.categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Date Range: From */}
          {(activeTab === "stock-ledger" ||
            activeTab === "damage-loss" ||
            activeTab === "repair" ||
            activeTab === "returns" ||
            activeTab === "transfer" ||
            activeTab === "reconciliation") && (
            <div>
              <label className="block text-[11px] font-semibold text-gray-600 mb-1">Date From</label>
              <input
                type="date"
                value={filters.dateFrom}
                onChange={(e) => handleFilterChange("dateFrom", e.target.value)}
                className="w-full py-1 px-2 text-xs border border-gray-300 rounded-md focus:border-[#b07d17] outline-none bg-white"
              />
            </div>
          )}

          {/* Date Range: To */}
          {(activeTab === "stock-ledger" ||
            activeTab === "damage-loss" ||
            activeTab === "repair" ||
            activeTab === "returns" ||
            activeTab === "transfer" ||
            activeTab === "reconciliation") && (
            <div>
              <label className="block text-[11px] font-semibold text-gray-600 mb-1">Date To</label>
              <input
                type="date"
                value={filters.dateTo}
                onChange={(e) => handleFilterChange("dateTo", e.target.value)}
                className="w-full py-1 px-2 text-xs border border-gray-300 rounded-md focus:border-[#b07d17] outline-none bg-white"
              />
            </div>
          )}

          {/* Ledger Type Filter */}
          {activeTab === "stock-ledger" && (
            <div>
              <label className="block text-[11px] font-semibold text-gray-600 mb-1">Transaction Type</label>
              <select
                value={filters.type}
                onChange={(e) => handleFilterChange("type", e.target.value)}
                className="w-full py-1.5 px-2 text-xs border border-gray-300 rounded-md focus:border-[#b07d17] outline-none bg-white"
              >
                <option value="all">All Types</option>
                <option value="Stock In">Stock In</option>
                <option value="Stock Out">Stock Out</option>
                <option value="Receipt">Receipt (Purchase)</option>
                <option value="Sale">Sale (Order)</option>
                <option value="Adjustment">Adjustment</option>
                <option value="Transfer">Transfer</option>
                <option value="Return">Return</option>
                <option value="Damage">Damage</option>
                <option value="Loss">Loss</option>
                <option value="Repair">Repair</option>
              </select>
            </div>
          )}

          {/* Low Stock Threshold */}
          {activeTab === "low-stock" && (
            <div>
              <label className="block text-[11px] font-semibold text-gray-600 mb-1">
                Reorder Threshold: ≤ {filters.threshold}
              </label>
              <div className="flex items-center gap-1">
                {[5, 10, 20, 50].map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => handleFilterChange("threshold", t.toString())}
                    className={`flex-1 py-1 text-xs rounded border font-semibold ${
                      filters.threshold === t.toString()
                        ? "bg-[#b07d17] text-white border-[#b07d17]"
                        : "bg-gray-50 text-gray-700 border-gray-300 hover:bg-gray-100"
                    }`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Valuation Basis */}
          {activeTab === "valuation" && (
            <div>
              <label className="block text-[11px] font-semibold text-gray-600 mb-1">Valuation Basis</label>
              <select
                value={filters.valuationBasis}
                onChange={(e) => handleFilterChange("valuationBasis", e.target.value)}
                className="w-full py-1.5 px-2 text-xs border border-gray-300 rounded-md focus:border-[#b07d17] outline-none bg-white font-medium"
              >
                <option value="retail">Retail M.R.P Basis</option>
                <option value="wholesale">Special / Wholesale Cost Basis</option>
              </select>
            </div>
          )}

          {/* Ageing Bucket Filter */}
          {activeTab === "ageing" && (
            <div>
              <label className="block text-[11px] font-semibold text-gray-600 mb-1">Age Bucket</label>
              <select
                value={filters.bucket}
                onChange={(e) => handleFilterChange("bucket", e.target.value)}
                className="w-full py-1.5 px-2 text-xs border border-gray-300 rounded-md focus:border-[#b07d17] outline-none bg-white"
              >
                <option value="all">All Buckets</option>
                <option value="0-30">0 – 30 Days</option>
                <option value="31-60">31 – 60 Days</option>
                <option value="61-90">61 – 90 Days</option>
                <option value="91-180">91 – 180 Days</option>
                <option value="180+">180+ Days (Stagnant)</option>
              </select>
            </div>
          )}

          {/* Current Stock Status Filter */}
          {activeTab === "current-stock" && (
            <div>
              <label className="block text-[11px] font-semibold text-gray-600 mb-1">Status</label>
              <select
                value={filters.status}
                onChange={(e) => handleFilterChange("status", e.target.value)}
                className="w-full py-1.5 px-2 text-xs border border-gray-300 rounded-md focus:border-[#b07d17] outline-none bg-white"
              >
                <option value="all">All Statuses</option>
                <option value="active">Active (In Stock)</option>
                <option value="out of stock">Out of Stock</option>
              </select>
            </div>
          )}
        </div>
      </div>

      {/* ─── DATA TABLE CONTAINER ─────────────────────────────────────────── */}
      <div className="table-container bg-white border border-[#D5D9D9] rounded-xl shadow-xs overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs border-collapse">
            <thead className="bg-[#f0f2f2] border-b border-[#D5D9D9] text-gray-700 uppercase tracking-wider text-[11px]">
              {renderTableHeaders(activeTab)}
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="12" className="py-12 text-center text-gray-500">
                    <div className="flex flex-col items-center justify-center gap-2">
                      <RefreshCw className="w-6 h-6 animate-spin text-[#b07d17]" />
                      <span className="text-xs font-semibold">Generating report intelligence...</span>
                    </div>
                  </td>
                </tr>
              ) : data.length === 0 ? (
                <tr>
                  <td colSpan="12" className="py-12 text-center text-gray-500">
                    <div className="flex flex-col items-center justify-center gap-2">
                      <Layers className="w-8 h-8 text-gray-400" />
                      <span className="text-sm font-semibold text-gray-700">No records found</span>
                      <p className="text-xs text-gray-500">Try adjusting your filters or search keywords.</p>
                      <button
                        onClick={handleResetFilters}
                        className="mt-2 text-xs px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded font-semibold transition"
                      >
                        Clear Filters
                      </button>
                    </div>
                  </td>
                </tr>
              ) : (
                data.map((row, idx) => renderTableRow(activeTab, row, idx))
              )}
            </tbody>
          </table>
        </div>

        {/* ─── PAGINATION BAR ─────────────────────────────────────────────── */}
        {!loading && data.length > 0 && (
          <div className="no-print px-4 py-3 bg-[#fafafa] border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-gray-600">
            <div className="flex items-center gap-2">
              <span>
                Showing <strong>{(page - 1) * limit + 1}</strong> to{" "}
                <strong>{Math.min(page * limit, totalItems)}</strong> of <strong>{totalItems}</strong> entries
              </span>
              <span className="text-gray-300">|</span>
              <span className="flex items-center gap-1">
                Per page:
                <select
                  value={limit}
                  onChange={(e) => {
                    setLimit(Number(e.target.value));
                    setPage(1);
                  }}
                  className="border border-gray-300 rounded px-1.5 py-0.5 bg-white text-xs outline-none"
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </span>
            </div>

            <div className="flex items-center gap-1">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="p-1.5 rounded border border-gray-300 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed transition"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="px-3 py-1 font-semibold text-gray-700">
                Page {page} of {totalPages}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="p-1.5 rounded border border-gray-300 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed transition"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

// ─── TABLE HEADERS RENDERER PER REPORT ────────────────────────────────────────
function renderTableHeaders(tab) {
  switch (tab) {
    case "current-stock":
      return (
        <tr>
          <th className="px-4 py-3">#</th>
          <th className="px-4 py-3">SKU</th>
          <th className="px-4 py-3">Product Name</th>
          <th className="px-4 py-3">Category</th>
          <th className="px-4 py-3">Warehouse</th>
          <th className="px-4 py-3">Rack/Bin</th>
          <th className="px-4 py-3">Owner</th>
          <th className="px-4 py-3">Vendor / Artisan</th>
          <th className="px-4 py-3 text-right">On-Hand Stock</th>
          <th className="px-4 py-3 text-right">Unit Price</th>
          <th className="px-4 py-3 text-right">Total Value</th>
          <th className="px-4 py-3">Status</th>
        </tr>
      );

    case "stock-ledger":
      return (
        <tr>
          <th className="px-4 py-3">Txn ID</th>
          <th className="px-4 py-3">Date & Time</th>
          <th className="px-4 py-3">Product Name</th>
          <th className="px-4 py-3">SKU</th>
          <th className="px-4 py-3">Type</th>
          <th className="px-4 py-3 text-right">Qty</th>
          <th className="px-4 py-3 text-right">Before</th>
          <th className="px-4 py-3 text-right">After</th>
          <th className="px-4 py-3">Warehouse</th>
          <th className="px-4 py-3">Performed By</th>
          <th className="px-4 py-3">Reference / Doc</th>
          <th className="px-4 py-3">Note / Reason</th>
        </tr>
      );

    case "low-stock":
      return (
        <tr>
          <th className="px-4 py-3">#</th>
          <th className="px-4 py-3">SKU</th>
          <th className="px-4 py-3">Product Name</th>
          <th className="px-4 py-3">Category</th>
          <th className="px-4 py-3">Warehouse</th>
          <th className="px-4 py-3">Owner</th>
          <th className="px-4 py-3">Vendor</th>
          <th className="px-4 py-3 text-right">Current Stock</th>
          <th className="px-4 py-3 text-right">Reorder Threshold</th>
          <th className="px-4 py-3 text-right">Deficit</th>
          <th className="px-4 py-3">Status</th>
        </tr>
      );

    case "valuation":
      return (
        <tr>
          <th className="px-4 py-3">#</th>
          <th className="px-4 py-3">SKU</th>
          <th className="px-4 py-3">Product Name</th>
          <th className="px-4 py-3">Category</th>
          <th className="px-4 py-3">Warehouse</th>
          <th className="px-4 py-3">Owner</th>
          <th className="px-4 py-3 text-right">On-Hand Stock</th>
          <th className="px-4 py-3 text-right">Unit Valuation</th>
          <th className="px-4 py-3">Valuation Basis</th>
          <th className="px-4 py-3 text-right">Holding Value (₹)</th>
        </tr>
      );

    case "ageing":
      return (
        <tr>
          <th className="px-4 py-3">#</th>
          <th className="px-4 py-3">SKU</th>
          <th className="px-4 py-3">Product Name</th>
          <th className="px-4 py-3">Category</th>
          <th className="px-4 py-3">Warehouse</th>
          <th className="px-4 py-3">Owner</th>
          <th className="px-4 py-3 text-right">Stock</th>
          <th className="px-4 py-3 text-right">Days in Stock</th>
          <th className="px-4 py-3">Age Bucket</th>
          <th className="px-4 py-3">Movement Status</th>
        </tr>
      );

    case "damage-loss":
      return (
        <tr>
          <th className="px-4 py-3">Txn ID</th>
          <th className="px-4 py-3">Date</th>
          <th className="px-4 py-3">Product Name</th>
          <th className="px-4 py-3">SKU</th>
          <th className="px-4 py-3">Discrepancy Type</th>
          <th className="px-4 py-3 text-right">Damaged Qty</th>
          <th className="px-4 py-3 text-right">Est. Loss (₹)</th>
          <th className="px-4 py-3">Warehouse / Location</th>
          <th className="px-4 py-3">Owner</th>
          <th className="px-4 py-3">Reported By</th>
          <th className="px-4 py-3">Reason / Disposition</th>
        </tr>
      );

    case "repair":
      return (
        <tr>
          <th className="px-4 py-3">Job ID</th>
          <th className="px-4 py-3">Date Sent</th>
          <th className="px-4 py-3">Product Name</th>
          <th className="px-4 py-3">SKU</th>
          <th className="px-4 py-3 text-right">Quantity</th>
          <th className="px-4 py-3">Warehouse Location</th>
          <th className="px-4 py-3">Job / RMA Reference</th>
          <th className="px-4 py-3">Technician / User</th>
          <th className="px-4 py-3">Repair Notes</th>
        </tr>
      );

    case "returns":
      return (
        <tr>
          <th className="px-4 py-3">Return ID</th>
          <th className="px-4 py-3">Date Received</th>
          <th className="px-4 py-3">Product Name</th>
          <th className="px-4 py-3">SKU</th>
          <th className="px-4 py-3 text-right">Returned Qty</th>
          <th className="px-4 py-3">Warehouse</th>
          <th className="px-4 py-3">Owner / Vendor</th>
          <th className="px-4 py-3">Order / RMA Ref</th>
          <th className="px-4 py-3">Received By</th>
          <th className="px-4 py-3">Return Reason & Disposition</th>
        </tr>
      );

    case "transfer":
      return (
        <tr>
          <th className="px-4 py-3">Transfer Txn</th>
          <th className="px-4 py-3">Date</th>
          <th className="px-4 py-3">Product Name</th>
          <th className="px-4 py-3">SKU</th>
          <th className="px-4 py-3 text-right">Transferred Qty</th>
          <th className="px-4 py-3">Source Warehouse</th>
          <th className="px-4 py-3">Destination Warehouse</th>
          <th className="px-4 py-3">Transferred By</th>
          <th className="px-4 py-3">Waybill / Doc Ref</th>
          <th className="px-4 py-3">Status</th>
        </tr>
      );

    case "reconciliation":
      return (
        <tr>
          <th className="px-4 py-3">Audit Txn</th>
          <th className="px-4 py-3">Date</th>
          <th className="px-4 py-3">Product Name</th>
          <th className="px-4 py-3">SKU</th>
          <th className="px-4 py-3 text-right">System Qty (Before)</th>
          <th className="px-4 py-3 text-right">Physical Count (After)</th>
          <th className="px-4 py-3 text-right">Variance Qty</th>
          <th className="px-4 py-3">Warehouse</th>
          <th className="px-4 py-3">Auditor / User</th>
          <th className="px-4 py-3">Approved Adjustment Note</th>
        </tr>
      );

    default:
      return null;
  }
}

// ─── TABLE ROWS RENDERER PER REPORT ───────────────────────────────────────────
function renderTableRow(tab, row, idx) {
  switch (tab) {
    case "current-stock":
      return (
        <tr key={row._id || idx} className="hover:bg-gray-50 transition border-b border-gray-100">
          <td className="px-4 py-2.5 text-gray-500 font-mono">{idx + 1}</td>
          <td className="px-4 py-2.5 font-mono font-medium text-gray-700">{row.sku}</td>
          <td className="px-4 py-2.5 font-medium text-gray-900 max-w-xs truncate">{row.name}</td>
          <td className="px-4 py-2.5 text-gray-600">{row.category}</td>
          <td className="px-4 py-2.5 text-gray-600">{row.warehouse}</td>
          <td className="px-4 py-2.5 font-mono text-gray-500">{row.rackBinLocation}</td>
          <td className="px-4 py-2.5">
            <span
              className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                row.owner === "Admin" ? "bg-purple-100 text-purple-700" : "bg-orange-100 text-orange-700"
              }`}
            >
              {row.owner}
            </span>
          </td>
          <td className="px-4 py-2.5 text-gray-600">{row.vendorName}</td>
          <td className="px-4 py-2.5 text-right font-bold text-gray-900">{row.stock}</td>
          <td className="px-4 py-2.5 text-right text-gray-700">₹{(row.price || 0).toLocaleString("en-IN")}</td>
          <td className="px-4 py-2.5 text-right font-semibold text-emerald-700">
            ₹{(row.totalValue || 0).toLocaleString("en-IN")}
          </td>
          <td className="px-4 py-2.5">
            <span
              className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                row.stock > 0 ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
              }`}
            >
              {row.stock > 0 ? "In Stock" : "Out of Stock"}
            </span>
          </td>
        </tr>
      );

    case "stock-ledger":
      return (
        <tr key={row._id || idx} className="hover:bg-gray-50 transition border-b border-gray-100">
          <td className="px-4 py-2.5 font-mono font-semibold text-blue-700">{row.transactionId}</td>
          <td className="px-4 py-2.5 text-gray-500 whitespace-nowrap">
            {new Date(row.date).toLocaleString("en-IN", {
              day: "2-digit",
              month: "short",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            })}
          </td>
          <td className="px-4 py-2.5 font-medium text-gray-900 max-w-xs truncate">{row.productName}</td>
          <td className="px-4 py-2.5 font-mono text-gray-600">{row.sku}</td>
          <td className="px-4 py-2.5">
            <BadgeType type={row.type} />
          </td>
          <td
            className={`px-4 py-2.5 text-right font-bold ${
              row.quantity > 0 ? "text-emerald-700" : row.quantity < 0 ? "text-red-600" : "text-gray-700"
            }`}
          >
            {row.quantity > 0 ? `+${row.quantity}` : row.quantity}
          </td>
          <td className="px-4 py-2.5 text-right text-gray-500">{row.beforeQuantity}</td>
          <td className="px-4 py-2.5 text-right font-semibold text-gray-900">{row.afterQuantity}</td>
          <td className="px-4 py-2.5 text-gray-600">{row.warehouse}</td>
          <td className="px-4 py-2.5 text-gray-700 font-medium">{row.performedBy}</td>
          <td className="px-4 py-2.5 font-mono text-gray-500 text-[11px]">{row.referenceId}</td>
          <td className="px-4 py-2.5 text-gray-500 text-xs max-w-xs truncate">{row.note}</td>
        </tr>
      );

    case "low-stock":
      return (
        <tr key={row._id || idx} className="hover:bg-gray-50 transition border-b border-gray-100">
          <td className="px-4 py-2.5 text-gray-500 font-mono">{idx + 1}</td>
          <td className="px-4 py-2.5 font-mono font-medium text-gray-700">{row.sku}</td>
          <td className="px-4 py-2.5 font-medium text-gray-900">{row.name}</td>
          <td className="px-4 py-2.5 text-gray-600">{row.category}</td>
          <td className="px-4 py-2.5 text-gray-600">{row.warehouse}</td>
          <td className="px-4 py-2.5 text-gray-600">{row.owner}</td>
          <td className="px-4 py-2.5 text-gray-600">{row.vendorName}</td>
          <td className="px-4 py-2.5 text-right font-black text-red-600">{row.stock}</td>
          <td className="px-4 py-2.5 text-right text-gray-600 font-semibold">{row.reorderThreshold}</td>
          <td className="px-4 py-2.5 text-right font-bold text-orange-600">-{row.shortage}</td>
          <td className="px-4 py-2.5">
            <span
              className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                row.stock <= 0 ? "bg-red-100 text-red-700 border border-red-200" : "bg-amber-100 text-amber-800"
              }`}
            >
              {row.status}
            </span>
          </td>
        </tr>
      );

    case "valuation":
      return (
        <tr key={row._id || idx} className="hover:bg-gray-50 transition border-b border-gray-100">
          <td className="px-4 py-2.5 text-gray-500 font-mono">{idx + 1}</td>
          <td className="px-4 py-2.5 font-mono font-medium text-gray-700">{row.sku}</td>
          <td className="px-4 py-2.5 font-medium text-gray-900">{row.name}</td>
          <td className="px-4 py-2.5 text-gray-600">{row.category}</td>
          <td className="px-4 py-2.5 text-gray-600">{row.warehouse}</td>
          <td className="px-4 py-2.5 text-gray-600">{row.owner}</td>
          <td className="px-4 py-2.5 text-right font-bold text-blue-700">{row.stock}</td>
          <td className="px-4 py-2.5 text-right font-medium text-gray-800">
            ₹{(row.unitValuation || 0).toLocaleString("en-IN")}
          </td>
          <td className="px-4 py-2.5 text-gray-500 text-[11px]">{row.valuationBasis}</td>
          <td className="px-4 py-2.5 text-right font-black text-emerald-800 text-sm">
            ₹{(row.totalValuation || 0).toLocaleString("en-IN")}
          </td>
        </tr>
      );

    case "ageing":
      return (
        <tr key={row._id || idx} className="hover:bg-gray-50 transition border-b border-gray-100">
          <td className="px-4 py-2.5 text-gray-500 font-mono">{idx + 1}</td>
          <td className="px-4 py-2.5 font-mono font-medium text-gray-700">{row.sku}</td>
          <td className="px-4 py-2.5 font-medium text-gray-900">{row.name}</td>
          <td className="px-4 py-2.5 text-gray-600">{row.category}</td>
          <td className="px-4 py-2.5 text-gray-600">{row.warehouse}</td>
          <td className="px-4 py-2.5 text-gray-600">{row.owner}</td>
          <td className="px-4 py-2.5 text-right font-bold text-gray-900">{row.stock}</td>
          <td className="px-4 py-2.5 text-right font-bold text-gray-800">{row.daysInStock} days</td>
          <td className="px-4 py-2.5">
            <span
              className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                row.ageBucket === "180+"
                  ? "bg-red-100 text-red-800"
                  : row.ageBucket === "91-180"
                  ? "bg-orange-100 text-orange-800"
                  : row.ageBucket === "61-90"
                  ? "bg-amber-100 text-amber-800"
                  : "bg-emerald-100 text-emerald-800"
              }`}
            >
              {row.ageBucket} Days
            </span>
          </td>
          <td className="px-4 py-2.5">
            {row.isSlowMoving ? (
              <span className="inline-flex items-center gap-1 text-[11px] font-bold text-red-600">
                <TrendingDown className="w-3.5 h-3.5 text-red-500" />
                Slow-Moving
              </span>
            ) : (
              <span className="text-[11px] font-semibold text-emerald-700">Healthy</span>
            )}
          </td>
        </tr>
      );

    case "damage-loss":
      return (
        <tr key={row._id || idx} className="hover:bg-gray-50 transition border-b border-gray-100">
          <td className="px-4 py-2.5 font-mono font-bold text-red-700">{row.transactionId}</td>
          <td className="px-4 py-2.5 text-gray-500 whitespace-nowrap">
            {new Date(row.date).toLocaleDateString("en-IN")}
          </td>
          <td className="px-4 py-2.5 font-medium text-gray-900">{row.productName}</td>
          <td className="px-4 py-2.5 font-mono text-gray-600">{row.sku}</td>
          <td className="px-4 py-2.5">
            <span
              className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                row.type === "Damage" ? "bg-amber-100 text-amber-800" : "bg-red-100 text-red-800"
              }`}
            >
              {row.type}
            </span>
          </td>
          <td className="px-4 py-2.5 text-right font-black text-red-600">{Math.abs(row.quantity)}</td>
          <td className="px-4 py-2.5 text-right font-bold text-gray-900">
            ₹{(row.totalValue || 0).toLocaleString("en-IN")}
          </td>
          <td className="px-4 py-2.5 text-gray-600">
            {row.warehouse} {row.locationCode ? `(${row.locationCode})` : ""}
          </td>
          <td className="px-4 py-2.5 text-gray-600">{row.owner}</td>
          <td className="px-4 py-2.5 text-gray-700">{row.performedBy}</td>
          <td className="px-4 py-2.5 text-gray-600 text-xs">{row.note}</td>
        </tr>
      );

    case "repair":
      return (
        <tr key={row._id || idx} className="hover:bg-gray-50 transition border-b border-gray-100">
          <td className="px-4 py-2.5 font-mono font-bold text-purple-700">{row.transactionId}</td>
          <td className="px-4 py-2.5 text-gray-500 whitespace-nowrap">
            {new Date(row.date).toLocaleDateString("en-IN")}
          </td>
          <td className="px-4 py-2.5 font-medium text-gray-900">{row.productName}</td>
          <td className="px-4 py-2.5 font-mono text-gray-600">{row.sku}</td>
          <td className="px-4 py-2.5 text-right font-bold text-purple-800">{Math.abs(row.quantity)}</td>
          <td className="px-4 py-2.5 text-gray-600">
            {row.warehouse} {row.locationCode ? `(${row.locationCode})` : ""}
          </td>
          <td className="px-4 py-2.5 font-mono text-gray-600">{row.referenceId}</td>
          <td className="px-4 py-2.5 text-gray-700">{row.performedBy}</td>
          <td className="px-4 py-2.5 text-gray-600 text-xs">{row.note}</td>
        </tr>
      );

    case "returns":
      return (
        <tr key={row._id || idx} className="hover:bg-gray-50 transition border-b border-gray-100">
          <td className="px-4 py-2.5 font-mono font-bold text-amber-700">{row.transactionId}</td>
          <td className="px-4 py-2.5 text-gray-500 whitespace-nowrap">
            {new Date(row.date).toLocaleDateString("en-IN")}
          </td>
          <td className="px-4 py-2.5 font-medium text-gray-900">{row.productName}</td>
          <td className="px-4 py-2.5 font-mono text-gray-600">{row.sku}</td>
          <td className="px-4 py-2.5 text-right font-bold text-amber-800">{Math.abs(row.quantity)}</td>
          <td className="px-4 py-2.5 text-gray-600">{row.warehouse}</td>
          <td className="px-4 py-2.5 text-gray-600">{row.vendorName || row.owner}</td>
          <td className="px-4 py-2.5 font-mono text-blue-700 font-semibold">{row.referenceId}</td>
          <td className="px-4 py-2.5 text-gray-700">{row.performedBy}</td>
          <td className="px-4 py-2.5 text-gray-600 text-xs">{row.note}</td>
        </tr>
      );

    case "transfer":
      return (
        <tr key={row._id || idx} className="hover:bg-gray-50 transition border-b border-gray-100">
          <td className="px-4 py-2.5 font-mono font-bold text-indigo-700">{row.transactionId}</td>
          <td className="px-4 py-2.5 text-gray-500 whitespace-nowrap">
            {new Date(row.date).toLocaleDateString("en-IN")}
          </td>
          <td className="px-4 py-2.5 font-medium text-gray-900">{row.productName}</td>
          <td className="px-4 py-2.5 font-mono text-gray-600">{row.sku}</td>
          <td className="px-4 py-2.5 text-right font-bold text-indigo-800">{Math.abs(row.quantity)}</td>
          <td className="px-4 py-2.5 text-gray-700 font-medium">{row.warehouse}</td>
          <td className="px-4 py-2.5 text-indigo-700 font-semibold">
            {row.warehouseTo || "Regional Distribution Center"}
          </td>
          <td className="px-4 py-2.5 text-gray-700">{row.performedBy}</td>
          <td className="px-4 py-2.5 font-mono text-gray-500 text-[11px]">{row.referenceId}</td>
          <td className="px-4 py-2.5">
            <span className="px-2 py-0.5 rounded bg-emerald-100 text-emerald-800 text-[10px] font-bold">
              Completed
            </span>
          </td>
        </tr>
      );

    case "reconciliation":
      return (
        <tr key={row._id || idx} className="hover:bg-gray-50 transition border-b border-gray-100">
          <td className="px-4 py-2.5 font-mono font-bold text-blue-800">{row.transactionId}</td>
          <td className="px-4 py-2.5 text-gray-500 whitespace-nowrap">
            {new Date(row.date).toLocaleDateString("en-IN")}
          </td>
          <td className="px-4 py-2.5 font-medium text-gray-900">{row.productName}</td>
          <td className="px-4 py-2.5 font-mono text-gray-600">{row.sku}</td>
          <td className="px-4 py-2.5 text-right text-gray-600 font-medium">{row.beforeQuantity}</td>
          <td className="px-4 py-2.5 text-right font-bold text-gray-900">{row.afterQuantity}</td>
          <td
            className={`px-4 py-2.5 text-right font-black ${
              row.quantity > 0 ? "text-emerald-700" : row.quantity < 0 ? "text-red-600" : "text-gray-500"
            }`}
          >
            {row.quantity > 0 ? `+${row.quantity}` : row.quantity}
          </td>
          <td className="px-4 py-2.5 text-gray-600">{row.warehouse}</td>
          <td className="px-4 py-2.5 text-gray-700 font-medium">{row.performedBy}</td>
          <td className="px-4 py-2.5 text-gray-600 text-xs">{row.note}</td>
        </tr>
      );

    default:
      return null;
  }
}

// ─── TRANSACTION BADGE HELPER ─────────────────────────────────────────────────
function BadgeType({ type }) {
  const styles = {
    "Stock In": "bg-emerald-100 text-emerald-800 border-emerald-200",
    "Stock Out": "bg-orange-100 text-orange-800 border-orange-200",
    Receipt: "bg-blue-100 text-blue-800 border-blue-200",
    Sale: "bg-purple-100 text-purple-800 border-purple-200",
    Adjustment: "bg-sky-100 text-sky-800 border-sky-200",
    Transfer: "bg-indigo-100 text-indigo-800 border-indigo-200",
    Return: "bg-yellow-100 text-yellow-800 border-yellow-200",
    Damage: "bg-amber-100 text-amber-800 border-amber-200",
    Loss: "bg-red-100 text-red-800 border-red-200",
    Repair: "bg-rose-100 text-rose-800 border-rose-200",
  };

  return (
    <span
      className={`px-2 py-0.5 rounded border text-[10px] font-bold ${
        styles[type] || "bg-gray-100 text-gray-700 border-gray-200"
      }`}
    >
      {type}
    </span>
  );
}

export default InventoryReports;
